import React, { Component, ErrorInfo, ReactNode } from 'react'

import { Button, Header, Icon, Image, Modal, Segment } from 'semantic-ui-react'

import vloggiLogo from '../_images/vloggi-logo-blue.png'
import rollbar from './Rollbar'
import './ErrorBoundary.scss'

interface Props {
  children: ReactNode
  fallback?: 'page' | 'modal'
}

interface State {
  hasError: boolean
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(_: Error): State {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
    rollbar.error('Uncaught React error', error, { componentStack: errorInfo.componentStack })
  }

  private retry = () => this.setState({ hasError: false })

  private reload = () => window.location.reload()

  private returnToProjects = () => window.location.assign('/projects')

  private renderModalFallback = () => (
    <Modal
      aria-labelledby="error-recovery-title"
      aria-modal="true"
      closeOnDimmerClick={false}
      closeOnEscape={false}
      dimmer="blurring"
      open
      role="dialog"
      size="small"
    >
      <Modal.Header id="error-recovery-title">
        <Icon name="warning circle" /> Something went wrong
      </Modal.Header>
      <Modal.Content>
        <p>We couldn't finish that action. Your existing project data should still be safe.</p>
        <p>Try opening this page again. If the problem continues, return to your projects and start again.</p>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={this.returnToProjects}>Back to projects</Button>
        <Button primary onClick={this.retry}>
          Try again
        </Button>
      </Modal.Actions>
    </Modal>
  )

  private renderPageFallback = () => (
    <main className="error-fallback" role="alert">
      <Segment className="error-fallback__card" raised textAlign="center">
        <Image className="error-fallback__logo" src={vloggiLogo} alt="Vloggi" />
        <Icon className="error-fallback__icon" name="warning circle" />
        <Header as="h1">Something went wrong</Header>
        <p>Studio couldn't load this page. Your existing project data should still be safe.</p>
        <p>Reload Studio to try again, or return to your projects.</p>
        <div className="error-fallback__actions">
          <Button onClick={this.returnToProjects}>Back to projects</Button>
          <Button primary onClick={this.reload}>
            Reload Studio
          </Button>
        </div>
      </Segment>
    </main>
  )

  public render() {
    if (this.state.hasError) {
      return this.props.fallback === 'modal' ? this.renderModalFallback() : this.renderPageFallback()
    }

    return this.props.children
  }
}

export default ErrorBoundary
